/**
 * WithdrawalPage Component
 * 앱웹 전용 출금 페이지 - VROONG 브랜드 통일 디자인
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../../../../core/components/BottomNavigation';
import { useWithdrawal } from '../hooks/useWithdrawal';
import { useMaintenanceGuard } from '../../../system/maintenance/hooks/useMaintenanceGuard';
import { commonCodeApi } from '../../../../core/api/commonCodeApi';
import { getTransactionHistory } from '../api/withdrawalApi';
import type { TransactionHistoryItem } from '../types/withdrawal';
import type { CommonCodeDto } from '../../../../core/types/commonCode.types';
import { getUserInfo } from '../../../../core/utils';
import styles from '../styles/WithdrawalPage.module.css';

const WithdrawalPage: React.FC = () => {
    const navigate = useNavigate();
    const userInfo = getUserInfo();
    const isSupplier = !!userInfo?.suppId;
    const { accountInfo, isLoading, error, isRequesting, requestError, handleWithdrawalRequest, fetchWithdrawalAccount } = useWithdrawal();
    const { canProceed } = useMaintenanceGuard();
    const [withdrawalAmount, setWithdrawalAmount] = useState<string>('');
    const [briefs, setBriefs] = useState<string>('');
    const [withdrawalFee, setWithdrawalFee] = useState<number>(300); // 기본값 300원
    const [showWithdrawalModal, setShowWithdrawalModal] = useState<boolean>(false); // 출금 모달 상태
    
    // 입출금 내역 관련 상태
    const [transactions, setTransactions] = useState<TransactionHistoryItem[]>([]);
    const [isLoadingTransactions, setIsLoadingTransactions] = useState<boolean>(false);
    const [transactionError, setTransactionError] = useState<Error | null>(null);
    const [wdTypeCodes, setWdTypeCodes] = useState<CommonCodeDto[]>([]);
    const [selectedFilter, setSelectedFilter] = useState<string>('');

    // 출금 수수료 조회
    useEffect(() => {
        const fetchFees = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('WITHRAW_FEE_AMT');
                if (response.codes && response.codes.length > 0) {
                    // 출금 수수료 조회 (comCode === "WITHRAW")
                    const withdrawalCode = response.codes.find(code => code.comCode === 'WITHRAW');
                    if (withdrawalCode && withdrawalCode.val1) {
                        setWithdrawalFee(parseInt(withdrawalCode.val1, 10));
                    }
                }
            } catch (error) {
                console.error('수수료 조회 실패:', error);
                // 에러 발생 시 기본값 유지
            }
        };

        fetchFees();
    }, []);
    
    // 공통코드 조회
    useEffect(() => {
        const fetchWdTypeCodes = async () => {
            const response = await commonCodeApi.getCommonCodeList('WD_TYPE');
            if (response.codes) {
                setWdTypeCodes(response.codes);
            }
        };

        fetchWdTypeCodes();
    }, []);

    // 입출금 내역 조회 (충분히 긴 날짜 범위로 모든 데이터 조회)
    const fetchTransactionHistory = useCallback(async () => {
        setIsLoadingTransactions(true);
        setTransactionError(null);

        try {
            // 충분히 긴 날짜 범위 설정 (예: 과거 5년 ~ 오늘)
            const today = new Date();
            const fiveYearsAgo = new Date(today);
            fiveYearsAgo.setFullYear(today.getFullYear() - 5);
            
            const formatDateForApi = (date: Date): string => {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const d = String(date.getDate()).padStart(2, '0');
                return `${y}${m}${d}`;
            };

            const params = {
                wdType: isAcctTypeFilter(selectedFilter) ? '' : selectedFilter,
                fromDate: formatDateForApi(fiveYearsAgo),
                toDate: formatDateForApi(today),
            };

            const response = await getTransactionHistory(params);

            if (Array.isArray(response)) {
                if (isAcctTypeFilter(selectedFilter)) {
                    const filteredData = response.filter(
                        (item) => item.acctType === selectedFilter
                    );
                    setTransactions(filteredData);
                } else {
                    setTransactions(response);
                }
            } else {
                setTransactionError(new Error('입출금 내역 조회에 실패했습니다.'));
            }
        } catch (error) {
            setTransactionError(error as Error);
        } finally {
            setIsLoadingTransactions(false);
        }
    }, [selectedFilter]);



    // acctDate를 YYYY-MM-DD 형식으로 포맷
    const formatAcctDate = (acctDate: string): string => {
        if (!acctDate || acctDate.length !== 8) {
            return acctDate;
        }
        const year = acctDate.substring(0, 4);
        const month = acctDate.substring(4, 6);
        const day = acctDate.substring(6, 8);
        return `${year}-${month}-${day}`;
    };

    // 필터 변경 시 내역 조회
    useEffect(() => {
        fetchTransactionHistory();
    }, [selectedFilter, fetchTransactionHistory]);

    // 출금 계좌 정보 포맷팅
    const formatAccountInfo = useCallback(() => {
        if (!accountInfo) return '';
        const bankName = accountInfo.bankName || accountInfo.bankCode || '';
        const accountNum = accountInfo.accountNum || '';
        const accName = accountInfo.accName || '';
        
        // 예금주명이 있으면 표시, 없으면 표시하지 않음
        if (accName) {
            return `${bankName} ${accountNum} ${accName}`;
        } else {
            return `${bankName} ${accountNum}`;
        }
    }, [accountInfo]);

    // 입력값에서 숫자만 추출
    const parseAmount = useCallback((value: string): number => {
        const numbers = value.replace(/[^0-9]/g, '');
        return numbers ? parseInt(numbers, 10) : 0;
    }, []);

    // acctType 기반 필터인지 확인
    const isAcctTypeFilter = (filter: string): boolean => {
        return ['RP', 'RW', 'RR', 'SW', 'SR', 'TO', 'TI'].includes(filter);
    };
    
    // 금액 포맷팅 (천단위 콤마)
    const formatAmount = useCallback((amount: number | null | undefined): string => {
        if (amount === null || amount === undefined) {
            return '0';
        }
        return amount.toLocaleString();
    }, []);
    
    // 날짜 포맷팅 (배열 [년, 월, 일, 시, 분, 초] -> YYYY-MM-DD HH:mm:ss)
    const formatDateTime = (processDate: number[]): string => {
        if (!Array.isArray(processDate) || processDate.length < 6) {
            return '';
        }
        const [year, month, day, hour, minute, second] = processDate;
        const y = String(year).padStart(4, '0');
        const m = String(month).padStart(2, '0');
        const d = String(day).padStart(2, '0');
        const h = String(hour).padStart(2, '0');
        const min = String(minute).padStart(2, '0');
        const s = String(second).padStart(2, '0');
        return `${y}-${m}-${d} ${h}:${min}:${s}`;
    };
    
    // acctType으로 타입명 가져오기
    const getAcctTypeName = (acctType: string): string => {
        if (acctType === 'RP' || acctType === 'RW' || acctType === 'RR' || acctType === 'SW' || acctType === 'SR') {
            return '적립금';
        }
        if (acctType === 'T' || acctType === 'TO' || acctType === 'TI') {
            return '송금';
        }
        if (acctType === 'F') {
            return '수수료';
        }
        const code = wdTypeCodes.find(c => c.comCode === acctType);
        return code ? code.comCdName : (acctType === 'W' ? '출금' : acctType === 'D' ? '입금' : '');
    };

    // acctType으로 거래명 가져오기
    const getTransactionName = (acctType: string, acctDate: string): string => {
        if (acctType === 'RP') {
            return `선정산 (${getSettlementPeriod(acctDate, 'pre')})`;
        }
        if (acctType === 'RW') {
            return `주정산 (${getSettlementPeriod(acctDate, 'weekly')})`;
        }
        if (acctType === 'RR') {
            return `주정산 (${getSettlementPeriod(acctDate, 'weekly')})`;
        }
        if (acctType === 'SW') {
            return `협력사 주정산 (${getSettlementPeriod(acctDate, 'weekly')})`;
        }
        if (acctType === 'SR') {
            return `협력사 주정산 (${getSettlementPeriod(acctDate, 'weekly')})`;
        }
        if (acctType === 'T') {
            return '송금';
        }
        if (acctType === 'TO') {
            return '송금 출금';
        }
        if (acctType === 'TI') {
            return '송금 입금';
        }
        if (acctType === 'F') {
            return '출금 수수료';
        }
        return getAcctTypeName(acctType);
    };

    // 정산 기간 추론 함수
    const getSettlementPeriod = (acctDate: string, settlementType: 'weekly' | 'pre'): string => {
        try {
            const year = parseInt(acctDate.substring(0, 4));
            const month = parseInt(acctDate.substring(4, 6)) - 1;
            const day = parseInt(acctDate.substring(6, 8));
            const settlementDate = new Date(year, month, day);

            if (settlementType === 'weekly') {
                const dayOfWeek = settlementDate.getDay();
                let startDate: Date;
                
                if (dayOfWeek === 3) {
                    startDate = new Date(settlementDate);
                } else {
                    const daysToSubtract = dayOfWeek < 3 ? dayOfWeek + 4 : dayOfWeek - 3;
                    startDate = new Date(settlementDate);
                    startDate.setDate(settlementDate.getDate() - daysToSubtract);
                }
                
                const endDate = new Date(startDate);
                endDate.setDate(startDate.getDate() + 6);
                
                const formatDate = (date: Date): string => {
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    return `${m}.${d}`;
                };
                
                return `${formatDate(startDate)}~${formatDate(endDate)}`;
            } else {
                const m = String(settlementDate.getMonth() + 1).padStart(2, '0');
                const d = String(settlementDate.getDate()).padStart(2, '0');
                return `${m}.${d}`;
            }
        } catch (error) {
            console.warn('정산 기간 계산 실패:', error);
            return '';
        }
    };

    // 입금취소 여부 확인
    const isDepositCancel = (acctType: string): boolean => {
        return acctType === 'C';
    };

    // acctType으로 입출금 구분
    const getTransactionType = (acctType: string, tranAmt: number): 'withdrawal' | 'deposit' | 'point' | 'deposit-cancel' | 'transfer-out' | 'transfer-in' | 'transfer' | 'fee' => {
        if (acctType === 'RP' || acctType === 'RW' || acctType === 'RR' || acctType === 'SW' || acctType === 'SR') {
            return 'point';
        }
        if (acctType === 'W') {
            return 'withdrawal';
        }
        if (acctType === 'D') {
            return 'deposit';
        }
        if (acctType === 'T') {
            return 'transfer';
        }
        if (acctType === 'TO') {
            return 'transfer-out';
        }
        if (acctType === 'TI') {
            return 'transfer-in';
        }
        if (acctType === 'F') {
            return 'fee';
        }
        return tranAmt < 0 ? 'withdrawal' : 'deposit';
    };

    // 거래 타입별 아이콘
    const getTransactionIcon = (type: string): string => {
        switch (type) {
            case 'withdrawal':
                return '💸';
            case 'deposit':
                return '💵';
            case 'point':
                return '🎁';
            case 'deposit-cancel':
                return '↩️';
            case 'transfer':
                return '💱';
            case 'transfer-out':
                return '📤';
            case 'transfer-in':
                return '📥';
            case 'fee':
                return '💳';
            default:
                return '💰';
        }
    };

    // 출금 금액 입력 핸들러
    const handleWithdrawalAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const numbers = value.replace(/[^0-9]/g, '');
        if (numbers) {
            const amount = parseInt(numbers, 10);
            setWithdrawalAmount(formatAmount(amount));
        } else {
            setWithdrawalAmount('');
        }
    }, [formatAmount]);

    // 출금 계산된 값들
    const calculatedValues = useMemo(() => {
        const requestAmount = parseAmount(withdrawalAmount); // 출금신청금액
        const feeAmount = requestAmount > 0 ? withdrawalFee : 0; // 출금수수료
        const totalDeduction = requestAmount > 0 ? requestAmount + feeAmount : 0; // 총 차감액 (잔액에서 차감)
        const availableBalance = accountInfo?.balAmt || 0; // 출금가능 잔액
        
        return {
            requestAmount,
            feeAmount,
            totalDeduction,
            availableBalance
        };
    }, [withdrawalAmount, withdrawalFee, accountInfo?.balAmt, parseAmount]);

    const { requestAmount, feeAmount, totalDeduction, availableBalance } = calculatedValues;

    // 출금 버튼 비활성화 조건 (요청금액 + 수수료가 잔액보다 크면 비활성화)
    const isWithdrawalDisabled = useMemo(() => {
        return isRequesting ||
            requestAmount <= 0 ||
            availableBalance <= 0 ||
            totalDeduction > availableBalance;
    }, [isRequesting, requestAmount, availableBalance, totalDeduction]);

    // 출금 신청 핸들러
    const handleWithdrawalSubmit = useCallback(async () => {
        // 중복 클릭 방지 - 이미 요청 중이면 무시
        if (isRequesting) {
            return; // 에러는 useWithdrawal에서 설정됨
        }

        if (requestAmount <= 0) {
            alert('출금 금액을 입력해주세요.');
            return;
        }

        if (accountInfo && totalDeduction > accountInfo.balAmt) {
            alert(`출금 금액 + 수수료(${formatAmount(totalDeduction)}원)가 출금 가능 잔액(${formatAmount(accountInfo.balAmt)}원)을 초과합니다.`);
            return;
        }

        // 출금 전 점검 상태 즉시 확인
        const canWithdraw = await canProceed();
        if (!canWithdraw) {
            alert('시스템 점검 중입니다. 잠시 후 다시 시도해주세요.');
            return;
        }

        const result = await handleWithdrawalRequest(requestAmount, briefs);
        
        // 중복 요청 에러는 requestError로 표시되므로 여기서는 처리하지 않음
        if (result === null && requestError) {
            // 에러 메시지는 모달에 표시됨
            return;
        }
        
        if (result && result.success) {
            alert(result.message || '출금 신청이 완료되었습니다.');
            // 성공 시 입력 필드 초기화
            setWithdrawalAmount('');
            setBriefs('');
            setShowWithdrawalModal(false); // 모달 닫기
            // 계좌 정보 다시 조회 (잔액 업데이트)
            await fetchWithdrawalAccount();
            // 입출금 내역 새로고침
            await fetchTransactionHistory();
        } else if (result && !result.success) {
            alert(result.message || '출금 신청에 실패했습니다.');
        }
    }, [isRequesting, requestAmount, totalDeduction, accountInfo, formatAmount, handleWithdrawalRequest, briefs, fetchWithdrawalAccount, canProceed, fetchTransactionHistory, requestError]);

    // 로딩 상태
    if (isLoading) {
        return (
            <div className={`screen active ${styles['withdrawal-screen']}`}>
                <div className={styles['withdrawal-header']}>
                    <div className={styles['header-top']}>
                        <h1 className={styles['page-title']}>출금</h1>
                    </div>
                </div>
                <div className={styles['withdrawal-container']}>
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']}></div>
                        <p className={styles['loading-text']}>계좌 정보를 불러오는 중...</p>
                    </div>
                </div>
                <BottomNavigation currentScreen="withdrawal" />
            </div>
        );
    }

    // 에러 상태
    if (error) {
        return (
            <div className={`screen active ${styles['withdrawal-screen']}`}>
                <div className={styles['withdrawal-header']}>
                    <div className={styles['header-top']}>
                        <h1 className={styles['page-title']}>출금</h1>
                    </div>
                </div>
                <div className={styles['withdrawal-container']}>
                    <div className={styles['error-section']}>
                        <div className={styles['error-icon']}>⚠️</div>
                        <p className={styles['error-text']}>
                            {error.message || '계좌 정보를 불러오는데 실패했습니다.'}
                        </p>
                    </div>
                </div>
                <BottomNavigation currentScreen="withdrawal" />
            </div>
        );
    }

    return (
        <div className={`screen active ${styles['withdrawal-screen']}`}>
            {/* 헤더 */}
            <div className={styles['withdrawal-header']}>
                <div className={styles['header-top']}>
                    <h1 className={styles['page-title']}>출금</h1>
                </div>
            </div>

            {/* 메인 컨테이너 (스크롤 가능 영역) */}
            <div className={styles['withdrawal-container']}>
                {/* 예치금 잔액 카드 */}
                <div className={styles['balance-section']}>
                    <div className={styles['balance-card']}>
                        <div className={styles['balance-label']}>
                            <span className={styles['balance-icon']}>💳</span>
                            예치금 잔액
                        </div>
                        <div className={styles['balance-value']}>
                            {accountInfo ? formatAmount(accountInfo.balAmt) : '0'}
                            <span className={styles['balance-unit']}>원</span>
                        </div>
                        <div className={styles['balance-actions']}>
                            <button 
                                className={`${styles['balance-btn']} ${styles['withdrawal']} ${styles['full-width']}`}
                                onClick={() => setShowWithdrawalModal(true)}
                            >
                                출금
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* 입출금 내역 리스트 */}
                {transactionError ? (
                    <div className={styles['error-section']}>
                        <div className={styles['error-icon']}>⚠️</div>
                        <p className={styles['error-text']}>
                            {transactionError.message || '입출금 내역을 불러오는데 실패했습니다.'}
                        </p>
                    </div>
                ) : isLoadingTransactions ? (
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']}></div>
                        <p className={styles['loading-text']}>입출금 내역을 불러오는 중...</p>
                    </div>
                ) : transactions.length === 0 ? (
                    <div className={styles['empty-section']}>
                        <div className={styles['empty-icon']}>📭</div>
                        <p className={styles['empty-text']}>입출금 내역이 없습니다.</p>
                    </div>
                ) : (
                    <div className={styles['transaction-list']}>
                        {transactions.map((transaction, index) => {
                            const isCancel = isDepositCancel(transaction.acctType);
                            const transactionType = isCancel ? 'deposit-cancel' : getTransactionType(transaction.acctType, transaction.tranAmt);
                            const transactionName = getTransactionName(transaction.acctType, transaction.acctDate);
                            const acctTypeName = getAcctTypeName(transaction.acctType);

                            return (
                                <div key={index} className={styles['transaction-item']}>
                                    <div className={`${styles['transaction-badge']} ${styles[transactionType]}`}>
                                        <span className={styles['badge-icon']}>{getTransactionIcon(transactionType)}</span>
                                        <span className={styles['badge-text']}>{acctTypeName}</span>
                                    </div>
                                    <div className={styles['transaction-details']}>
                                        <div className={styles['transaction-header-row']}>
                                            <div className={styles['transaction-name']}>{transactionName}</div>
                                            <div className={`${styles['transaction-amount']} ${styles[transactionType]} ${transactionType === 'transfer' ? (transaction.tranAmt > 0 ? styles['positive'] : styles['negative']) : ''}`}>
                                                {transaction.tranAmt < 0 ? '-' : '+'}{formatAmount(Math.abs(transaction.tranAmt))} 원
                                            </div>
                                        </div>
                                        <div className={styles['transaction-footer-row']}>
                                            <div className={styles['transaction-date']}>
                                                {formatDateTime(transaction.insDate)}
                                            </div>
                                            <div className={styles['transaction-balance']}>
                                                잔액: {formatAmount(transaction.balAmt)} 원
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {/* withdrawal-container 닫기 */}

            {/* 출금 신청 모달 */}
            {showWithdrawalModal && (
                <div className={styles['modal-overlay']} onClick={() => setShowWithdrawalModal(false)}>
                    <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
                        <div className={styles['modal-header']}>
                            <h2 className={styles['modal-title']}>
                                <span className={styles['modal-title-icon']}>💸</span>
                                출금 신청
                            </h2>
                            <button 
                                className={styles['modal-close-btn']}
                                onClick={() => setShowWithdrawalModal(false)}
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className={styles['modal-body']}>
                            {/* 금액 입력 */}
                            <div className={styles['amount-input-wrapper']}>
                                <label className={styles['amount-input-label']}>출금 금액</label>
                                <input
                                    type="text"
                                    placeholder="0 원"
                                    className={styles['amount-input']}
                                    value={withdrawalAmount}
                                    onChange={handleWithdrawalAmountChange}
                                />
                            </div>
                            {/* 출금 안내 메시지 */}
                            <div className={styles['withdrawal-info-message']}>
                                <div className={styles['withdrawal-info-header']}>
                                    <span className={styles['withdrawal-info-icon']}>💡</span>
                                    <span className={styles['withdrawal-info-title']}>출금 안내</span>
                                </div>
                                <div className={styles['withdrawal-info-text']}>
                                    • 출금 시 수수료 {formatAmount(withdrawalFee)}원이 부과됩니다.<br/>
                                </div>
                            </div>

                            {/* 계산 상세 */}
                            <div className={styles['calculation-card']}>
                                <div className={styles['calc-row']}>
                                    <span className={styles['calc-label']}>출금신청금액</span>
                                    <span className={styles['calc-value']}>{formatAmount(requestAmount)} 원</span>
                                </div>
                                <div className={styles['calc-row']}>
                                    <span className={styles['calc-label']}>출금수수료</span>
                                    <span className={styles['calc-value']}>{formatAmount(feeAmount)} 원</span>
                                </div>
                                <div className={`${styles['calc-row']} ${styles['total']}`}>
                                    <span className={styles['calc-label']}>총 잔액 차감</span>
                                    <span className={styles['calc-value']}>{formatAmount(totalDeduction)} 원</span>
                                </div>
                            </div>

                            {/* 계좌 정보 */}
                            <div className={styles['account-info-card']}>
                                <div className={styles['account-info-header']}>
                                    <span className={styles['account-icon']}>🏦</span>
                                    <span className={styles['account-info-label']}>계좌정보</span>
                                </div>
                                <div className={styles['account-info-value']}>
                                    {formatAccountInfo() || '계좌 정보 없음'}
                                </div>
                            </div>

                            {/* 에러 메시지 */}
                            {requestError && (
                                <div className={styles['error-message']}>
                                    <span className={styles['error-message-text']}>{requestError.message}</span>
                                </div>
                            )}

                            {/* 버튼들 */}
                            <div className={styles['modal-actions']}>
                                <button
                                    className={`${styles['modal-btn']} ${styles['cancel']}`}
                                    type="button"
                                    onClick={() => setShowWithdrawalModal(false)}
                                >
                                    취소
                                </button>
                                <button
                                    className={`${styles['modal-btn']} ${styles['primary']}`}
                                    type="button"
                                    onClick={handleWithdrawalSubmit}
                                    disabled={isWithdrawalDisabled}
                                >
                                    {isRequesting ? '신청 중...' : '출금하기'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 하단 네비게이션 */}
            <BottomNavigation currentScreen="withdrawal" />
        </div>
    );
};

export default WithdrawalPage;
